import type { FC } from "react";
import { Controller } from "react-hook-form";

import { useTranslations } from "@ui/providers/useTranslations";
import { getDeliveryLimitCitiesDropdownListV2 } from "@ui/utils/getDeliveryLimitCitiesDropdownListV2/getDeliveryLimitCitiesDropdownListV2";

import type { LocationServiceAutocompleteService } from "@jti/fe-common";
import type { IGetOrderDeliveryLimits, PostCodeConfig } from "@jti/magento";

import { CityField, CountryField } from "../AddressFormProvider";
import { useFormContext } from "../Form";
import { TextInput } from "../Inputs/TextInput";
import { PostCode } from "../PostCode";
import { StreetAddressFields } from "../StreetAddressFields";

import styles from "./AsianAddressForm.module.scss";

export type AsianAddressFormProps = {
  fieldName?(name: string): string;
  enableStreetNameAutocomplete?: boolean;
  orderDeliveryLimits?: IGetOrderDeliveryLimits;
  postCodeConfig?: PostCodeConfig;
  locationServiceAutocompleteProvider?: LocationServiceAutocompleteService;
};

export const AsianAddressForm: FC<AsianAddressFormProps> = ({
  fieldName = (name) => name,
  enableStreetNameAutocomplete,
  orderDeliveryLimits,
  postCodeConfig,
  locationServiceAutocompleteProvider,
}) => {
  const { __ } = useTranslations();
  const form = useFormContext();
  const isCityLimitationEnabled = orderDeliveryLimits?.mode === "yes_city";
  const deliveryLimitCitiesDropdownList = getDeliveryLimitCitiesDropdownListV2(orderDeliveryLimits?.limits);
  const handleOnUpdateStreet = () => {
    form.trigger();
  };

  return (
    <div className={styles.container}>
      <StreetAddressFields
        fieldName={fieldName}
        onUpdateStreet={handleOnUpdateStreet}
        enableStreetNameAutocomplete={enableStreetNameAutocomplete}
        locationServiceAutocompleteProvider={locationServiceAutocompleteProvider}
      />
      <div className={styles.addressFields}>
        <Controller
          control={form.control}
          name={fieldName("districtName")}
          rules={{ required: __("form_validation.field_required") }}
          render={({ field, fieldState: { error } }) => {
            return (
              <TextInput
                {...field}
                required
                data-testid="districtInput"
                label={__("address_fields.district")}
                placeholder={__("address_fields.district_placeholder")}
                isError={!!error}
                errorText={error?.message}
                autoComplete="address-level3"
              />
            );
          }}
        />
        <CityField
          fieldName={fieldName}
          isCityLimitationEnabled={isCityLimitationEnabled}
          deliveryLimitCitiesDropdownList={deliveryLimitCitiesDropdownList}
          orderDeliveryLimits={orderDeliveryLimits}
        />
        <Controller
          control={form.control}
          name={fieldName("provinceName")}
          rules={{ required: __("form_validation.field_required") }}
          render={({ field, fieldState: { error } }) => {
            return (
              <TextInput
                {...field}
                required
                data-testid="provinceName"
                label={__("address_fields.province")}
                placeholder={__("address_fields.province_placeholder")}
                isError={!!error}
                errorText={error?.message}
                autoComplete="address-level1"
              />
            );
          }}
        />
        <PostCode fieldName={fieldName} config={postCodeConfig} />
        <CountryField fieldName={fieldName} />
      </div>
    </div>
  );
};
